package com.iregained.eventapp.components.sections.event_details.sections

import androidx.compose.runtime.*
import com.iregained.eventapp.components.widgets.SectionHeader
import com.iregained.eventapp.theme.SubheadlineTextStyle
import com.iregained.eventapp.theme.toSitePalette
import com.iregained.eventapp.utils.LocalBreakpoint
import com.iregained.eventapp.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATIONS
@Composable
private fun Modifier.hexPop(isVisible: Boolean, col: Int, row: Int): Modifier {
    // Delay based on geometric position to create a spreading "hive" build effect
    val delay = 200 + ((col + row) * 100)
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.5f).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delay.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delay.ms)
    )
}

@Composable
private fun Modifier.slideFromRight(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 40.px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun ClinicalApplicationsSection(data: ClinicalApplicationsData = defaultClinicalApplicationsData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val isXlDesktop = breakpoint >= Res.Breakpoints.XL
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinical-applications-section"

    Box(
        modifier = Modifier.fillMaxWidth().backgroundColor(palette.custom.lightGray)
        .id(targetId) // ADDED: ID for smooth scrolling
        .styleModifier { property("scroll-margin-top", "100px") }
        .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent),
        contentAlignment = Alignment.Center) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).toAttrs {
                    ref { element ->
                        val observer = IntersectionObserver { entries, obs ->
                            entries.forEach {
                                if (it.isIntersecting) {
                                    isVisible = true; obs.unobserve(element)
                                }
                            }
                        }
                        observer.observe(element); onDispose { observer.disconnect() }
                    }
                }) {

            Column(
                modifier = Modifier.fillMaxWidth().maxWidth(1440.px), horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SectionHeader(
                    text = data.header,
                    fontSize = 32.px,
                    lineWidthPercent = 25.percent,
                    modifier = Modifier.margin(bottom = 60.px),
                    alignment = Alignment.CenterHorizontally
                )

                if (isDesktop) {
                    val desktopGap = if (isXlDesktop) 80.px else 40.px
                    val graphicScale = if (isXlDesktop) 1.3f else 1f

                    Row(
                        modifier = Modifier.fillMaxWidth().gap(desktopGap),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        Box(modifier = Modifier.width(55.percent), contentAlignment = Alignment.Center) {
                            HoneycombGraphic(data.hexagons, scale = graphicScale, isVisible = isVisible)
                        }

                        Div(
                            attrs = SubheadlineTextStyle.toModifier().width(40.percent)
                                .fontSize(if (isXlDesktop) 20.px else 18.px).lineHeight(1.5).textAlign(TextAlign.Start)
                                .color(palette.custom.darkText)
                                .slideFromRight(isVisible, 400).toAttrs {
                                    ref { htmlElement -> htmlElement.innerHTML = data.descriptionHtml; onDispose {} }
                                })
                    }
                } else {
                    // DYNAMIC FIX: Determine if we are on a very small phone screen and scale the hex graphic down
                    val mobileScale = if (breakpoint < Res.Breakpoints.MD) 0.75f else 1f

                    Column(
                        modifier = Modifier.fillMaxWidth().maxWidth(800.px),
                        verticalArrangement = Arrangement.spacedBy(50.px),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Box(
                            // DYNAMIC FIX: Removed the Overflow.Auto scrollbar, graphic now shrinks to fit!
                            modifier = Modifier.fillMaxWidth().margin(bottom = 40.px),
                            contentAlignment = Alignment.Center
                        ) {
                            HoneycombGraphic(data.hexagons, scale = mobileScale, isVisible = isVisible)
                        }
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().fillMaxWidth().fontSize(20.px).lineHeight(1.5)
                                .color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).slideFromRight(isVisible, 200).toAttrs {
                                    ref { htmlElement -> htmlElement.innerHTML = data.descriptionHtml; onDispose {} }
                                })
                    }
                }
            }
        }
    }
}

@Composable
private fun HoneycombGraphic(hexagons: List<HexagonData>, scale: Float, isVisible: Boolean) {
    val hexW = (130 * scale)
    val hexH = (112 * scale)
    val colOffsetX = hexW * 0.75
    val rowOffsetY = hexH * 0.5
    val containerWidth = (3 * colOffsetX) + hexW
    val containerHeight = (3 * rowOffsetY) + hexH

    Box(
        modifier = Modifier.width(containerWidth.px).height(containerHeight.px)
            .styleModifier { property("filter", "drop-shadow(0px 4px 6px rgba(0,0,0,0.15))") }) {
        hexagons.forEach { hex ->
            key(hex.text) {
                val xPos = hex.col * colOffsetX
                val yPos = hex.row * rowOffsetY

                Box(
                    modifier = Modifier.width(hexW.px).height(hexH.px).position(Position.Absolute).top(yPos.px)
                        .left(xPos.px).backgroundColor(hex.bgColor).styleModifier {
                            property("clip-path", "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)")
                        }.padding(leftRight = 12.px).hexPop(isVisible, hex.col, hex.row),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = hex.text,
                        modifier = Modifier.color(hex.textColor).textAlign(TextAlign.Center).fontSize((12 * scale).px)
                            .fontWeight(FontWeight.Bold).whiteSpace(WhiteSpace.PreLine)
                    )
                }
            }
        }
    }
}

data class HexagonData(
    val text: String, val bgColor: CSSColorValue, val textColor: CSSColorValue, val col: Int, val row: Int
)

data class ClinicalApplicationsData(
    val sectionId: String? = null, val header: String, val descriptionHtml: String, val hexagons: List<HexagonData>
)

val defaultClinicalApplicationsData = ClinicalApplicationsData(
    sectionId = "clinical-applications-section",
    header = "BROAD SET OF CLINICAL APPLICATIONS",
    descriptionHtml = "The MyHand<sup>®</sup> System can be used to treat a wide range of hand function deficits including musculoskeletal or neurological injuries including <strong>Stroke, TBI or other hand injuries</strong>",
    hexagons = listOf(
        // ROW 0
        HexagonData("Neuromuscular\nDisorders\n(e.g. MS)", Color.rgb(139, 141, 140), Colors.White, col = 0, row = 0),
        HexagonData("SPORTS\nINJURIES", Color.rgb(244, 180, 26), Colors.White, col = 2, row = 0),

        // ROW 1
        HexagonData("Combat\nInjuries", Color.rgb(31, 97, 96), Colors.White, col = 1, row = 1),
        HexagonData("Traumatic\nBrain\nInjury", Color.rgb(31, 97, 96), Colors.White, col = 3, row = 1),

        // ROW 2
        HexagonData("Ageing\nrelated\ndisorders", Color.rgb(244, 180, 26), Colors.White, col = 0, row = 2),
        HexagonData("STROKE", Colors.White, Color.rgb(50, 50, 50), col = 2, row = 2),

        // ROW 3
        HexagonData("SYSTEMIC\nDISORDERS", Color.rgb(139, 141, 140), Colors.White, col = 1, row = 3),
        HexagonData("Musculoskeletal\nDisorders", Color.rgb(31, 97, 96), Colors.White, col = 3, row = 3)
    )
)